import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import StatsService from '@/services/StatsService';
import { extractError } from '@/helpers/extractApiError';

const model = {
    products: [],
    totalItems: 0,
    graph: [],
    dashboardCards: [],
    reportCards: [],
    usersByLocation: [],
    customerListOptions: [],
    loading: false,
    error: null,
};

export const getDashboardCards = createAsyncThunk(
    'stats/getDashboardCards',
    async (_, { rejectWithValue }) => {
        try {
            const response = await StatsService.getDashboardCards();
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getUsersByLocation = createAsyncThunk(
    'stats/getUsersByLocation',
    async (_, { rejectWithValue }) => {
        try {
            const response = await StatsService.getUsersByLocation();
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getGraphData = createAsyncThunk(
    'stats/getGraphData',
    async (_, { rejectWithValue }) => {
        try {
            const response = await StatsService.getGraphData();
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getProducts = createAsyncThunk(
    'stats/getProducts',
    async (data, { rejectWithValue }) => {
        try {
            const response = await StatsService.getProducts(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getReportCards = createAsyncThunk(
    'stats/getReportCards',
    async (data, { rejectWithValue }) => {
        try {
            const response = await StatsService.getReportCards(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getCustomerListOptions = createAsyncThunk(
    'stats/getCustomerListOptions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await StatsService.getCustomerListOptions();
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

const statsSlice = createSlice({
    name: 'stats',
    initialState: model,
    reducers: {},
    extraReducers: (builder) => {
        // get dashboard cards
        builder.addCase(getDashboardCards.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getDashboardCards.fulfilled, (state, action) => {
            state.loading = false;
            state.dashboardCards = action.payload;
        });
        builder.addCase(getDashboardCards.rejected, (state, action) => {
            state.loading = false;
            state.error = extractError(action);
        });
        // users by location
        builder.addCase(getUsersByLocation.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getUsersByLocation.fulfilled, (state, action) => {
            state.loading = false;
            state.usersByLocation = action.payload;
        });
        builder.addCase(getUsersByLocation.rejected, (state, action) => {
            state.loading = false;
            state.error = extractError(action);
        });
        // get graph data
        builder.addCase(getGraphData.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getGraphData.fulfilled, (state, action) => {
            state.loading = false;
            state.graph = action.payload;
        });
        builder.addCase(getGraphData.rejected, (state, action) => {
            state.loading = false;
            state.error = extractError(action);
        });
        // get products
        builder.addCase(getProducts.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getProducts.fulfilled, (state, action) => {
            state.loading = false;
            state.products = action.payload.products;
            state.totalItems = action.payload.total;
        });
        builder.addCase(getProducts.rejected, (state, action) => {
            state.loading = false;
            state.error = extractError(action);
        });
        // get reports cards
        builder.addCase(getReportCards.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getReportCards.fulfilled, (state, action) => {
            state.loading = false;
            state.reportCards = action.payload;
        });
        builder.addCase(getReportCards.rejected, (state, action) => {
            state.loading = false;
            state.error = extractError(action);
        });
        // customer list options
        builder.addCase(getCustomerListOptions.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getCustomerListOptions.fulfilled, (state, action) => {
            state.loading = false;
            state.customerListOptions = action.payload;
        });
        builder.addCase(getCustomerListOptions.rejected, (state, action) => {
            state.loading = false;
            state.error = extractError(action);
        });
    },
});

export default statsSlice.reducer;
